<template>
  <section class="new-flow">
    <application-menu :app="app" :flow="flow"></application-menu>
    <form class="ui form" autocomplete="off">
      <div class="ui top attached segment">
        <h3 class="ui horizontal left aligned divider header">
          {{ $t('CREATE', { thing: $tc('FLOW', 1) }) }}
        </h3>
        <div class="ui basic segment">
          <!-- DETAILS -->
          <div class="field required" :class="{ error: $v.flow.name.$error }">
            <label for="flowName">{{ $t('NAME') }}</label>
            <input
              id="flowName"
              type="text"
              :placeholder="$t('NAME')"
              v-model.trim="flow.name"
              @click="$v.flow.name.$touch()"
            />
          </div>

          <div class="inline fields">
            <!-- TYPE -->
            <label>{{ $tc('TYPE', 1) }}</label>
            <div class="field" v-for="(config, type) in defaultTypeConfig" :key="type">
              <div class="ui radio checkbox">
                <input
                  type="radio"
                  name="flowType"
                  :id="'flowType' + type"
                  :value="type"
                  v-model="flow.type"
                />
                <label :for="'flowType' + type">{{ $tc(type, 1) }}</label>
              </div>
            </div>
          </div>

          <div class="fields">
            <div class="two wide field" v-if="flow.type === 'STATE'">
              <typeahead
                :shouldFilterItems="false"
                :items="actionTypes"
                :current-item="flow.typeConfig.action"
                :label-text="$tc('ACTION', 1)"
                :placeholder-text="$tc('ACTION', 1)"
                :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('ACTION', 2) })"
                :select-handler="actionTypeSelected"
                :remove-handler="actionTypeRemoved"
              />
            </div>
            <div
              class="field"
              v-for="(config, configProperty) in defaultTypeConfig[flow.type]"
              :key="configProperty"
              v-if="typeof config === 'string' && shouldDisplayGenericInputFor(configProperty)"
            >
              <label :for="'flow' + configProperty">
                {{ $tc(configProperty.toUpperCase(), 1) }}
              </label>
              <input
                :id="'flow' + configProperty"
                type="text"
                :placeholder="$tc(configProperty.toUpperCase(), 1)"
                v-model="flow.typeConfig[configProperty]"
              />
            </div>
          </div>

          <div class="inline fields">
            <!-- LANGUAGE -->
            <label>{{ $tc('LANGUAGE', 1) }}</label>
            <div class="field" v-for="(name, capsName) in supportedLanguages" :key="capsName">
              <div class="ui radio checkbox">
                <input
                  type="radio"
                  name="flowLanguage"
                  :id="'flowLanguage' + name"
                  :value="name"
                  v-model="flow.language"
                />
                <label :for="'flowLanguage' + name">{{ $tc('LANGUAGE_' + capsName, 1) }}</label>
              </div>
            </div>
          </div>
        </div>

        <!-- OWNERSHIP -->
        <h4 class="ui horizontal left aligned divider header">{{ $t('OWNERSHIP_HEADER') }}</h4>
        <div class="ui basic segment">
          <div class="two fields">
            <div class="ten wide field">
              <group-typeahead
                :allGroups="groups"
                :currentGroups="currentGroups"
                @selected="groupSelected"
                @removed="groupRemoved"
                @created="groupCreated"
                :disabled="!groups"
                v-if="!isModuleTypeFlow"
              />
            </div>
            <div class="six wide field">
              <typeahead
                :items="users"
                :current-item="flowMaintainer.name"
                :label-text="$tc('MAINTAINER', 1)"
                :placeholder-text="$tc('MAINTAINER', 1)"
                :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('MAINTAINER', 2) })"
                :select-handler="maintainerSelected"
                :remove-handler="maintainerSelected"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="ui bottom attached tabular menu" style="padding-top: 8px;">
        <div class="right secondary menu">
          <loading-button class="ui right labeled icon button" :onClick="createFlow">
            {{ $t('CREATE', { thing: '' }) }}
            <i class="checkmark icon"></i>
          </loading-button>
        </div>
      </div>
    </form>
  </section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import { Auth, Application, Flow, FlowEditor, Group, User, Toast, Typenames } from 'services'
import { GroupTypeahead, Typeahead, LoadingButton } from 'ui'
import ApplicationMenu from './../ApplicationMenu'
import { required } from 'vuelidate/lib/validators'
import _ from 'lodash'

const flow = {
  name: '',
  inputs: [],
  outputs: [],
  application: '',
  code: '',
  editorCode: '',
  blocklyXml: '',
  maintainer: '',
  type: 'ACTION',
  language: Flow.supportedLanguages.ES5, // For now, default new flows to ES5
  typeConfig: {},
  icon: '',
  usesfields: []
}

export default {
  name: 'new-flow',
  components: { ApplicationMenu, LoadingButton, Typeahead, GroupTypeahead },

  props: {
    appId: {
      type: String,
      default: () => ''
    }
  },

  data() {
    return {
      app: {},
      flow: { ...flow },
      currentGroups: [],
      actionTypes: [
        { id: 'GET', name: 'GET' },
        { id: 'SET', name: 'SET' },
        { id: 'MAP', name: 'MAP' }
      ],
      defaultTypeConfig: Flow.defaultFlowTypeConfig,
      savePending: false,
      supportedLanguages: Flow.supportedLanguages
    }
  },

  validations: {
    flow: {
      name: {
        required
      }
    }
  },

  computed: {
    users() {
      return this.$store.state.user.items
    },
    groups() {
      return this.$store.state.group.items
    },
    flowMaintainer() {
      return _.find(this.users, { id: this.flow.maintainer }) || {}
    },
    isModuleTypeFlow() {
      return this.flow.type === 'MODULE'
    },
    savesRequested() {
      return this.$store.state.savesRequested
    }
  },

  methods: {
    shouldDisplayGenericInputFor(name: string) {
      return FlowEditor.shouldDisplayGenericInputFor(name)
    },

    readApp() {
      return Application.readApp(this.appId).then(app => {
        this.app = app
        return app
      })
    },

    createFlowSafely() {
      if (!this.savePending) this.createFlow()
    },

    createFlow() {
      if (!this.$v.$invalid) {
        const appId = this.$route.params.appId
        let flowId
        this.savePending = true
        return Flow.createFlow({
          ...this.flow,
          application: appId
        })
          .then(res => {
            flowId = res.id
            this.flow.version = res.version
            const promises = this.currentGroups.map(group =>
              Group.addAffiliate('flow', group.id, flowId)
            )

            return Promise.all(promises)
          })
          .then(() => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('FLOW', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'app-flow',
              params: { appId: appId, flowId: flowId, mode: 'auto' }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('FLOW', 1) })
              })
            )
            console.error(err)
          })
          .finally(() => {
            this.savePending = false
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('FLOW', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    createGroup(group) {
      return Group.createGroup(group)
        .then(res => {
          Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('GROUP', 1) }), {
            remove: 5000
          })
          return res.id
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('GROUP', 1) })
            })
          )
          console.error(err)
        })
    },

    groupSelected(group) {
      const affiliationExists = this.currentGroups.some(g => group.id === g.id)

      if (!affiliationExists) this.currentGroups.push(group)
    },

    groupRemoved(group) {
      this.currentGroups = this.currentGroups.filter(g => group.id !== g.id)
    },

    groupCreated(groupName) {
      return this.createGroup({ name: groupName }).then(groupId => {
        const newGroup = {
          id: groupId,
          name: groupName
        }
        this.currentGroups.push(newGroup)
        return newGroup
      })
    },

    maintainerSelected(maintainer) {
      if (maintainer) this.flow.maintainer = maintainer.id
      else this.flow.maintainer = ''
    },

    actionTypeSelected(action) {
      this.flow.typeConfig.action = action.name
    },

    actionTypeRemoved() {
      this.flow.typeConfig.action = ''
    },
    setLoggedInUserAsDefaultMaintainer(users) {
      let defaultMaintainer = _.find(users, { name: Auth.getLoggedInUser() })
      this.flow.maintainer = defaultMaintainer ? defaultMaintainer.id : ''
    }
  },
  created() {
    User.readAllUsers().then(this.setLoggedInUserAsDefaultMaintainer)
    Group.readAllGroups()
  },
  mounted() {
    this.readApp()
  },
  watch: {
    savesRequested() {
      this.createFlowSafely()
    }
  }
}
</script>
